import { useEffect, useState } from "react";
import { Download, Share, X, MoreVertical } from "lucide-react";
import { usePwaInstall } from "@/lib/pwaInstall";

// "Keep popping until they install" — dismiss only suppresses the banner for
// 1 hour, then it comes back automatically. The ONLY way to permanently hide
// it is to actually install the app (which makes display-mode become
// "standalone"). Even if they install then uninstall later, the banner
// re-appears the next time they hit the catalog.
const DISMISS_KEY = "sv_pwa_dismiss_until";
const DISMISS_MS = 60 * 60 * 1000; // 1 hour

function readDismissedUntil() {
  try {
    const until = Number(localStorage.getItem(DISMISS_KEY) || 0);
    // Migration: the old code stored 7-DAY cooldowns. If the saved timestamp
    // is more than 1 h in the future (longer than the current cooldown), it
    // must be from the old behaviour — clear it so the banner reappears now.
    if (until > Date.now() + DISMISS_MS + 60_000) {
      localStorage.removeItem(DISMISS_KEY);
      return 0;
    }
    return until;
  } catch {
    return 0;
  }
}

/**
 * Persistent install banner. Three render paths:
 *  - Android Chrome / Edge / Brave with `beforeinstallprompt`: shows a one-tap
 *    Install button.
 *  - Android browsers where the event didn't fire (Firefox, already-dismissed
 *    by the browser): shows manual instructions ("Tap ⋮ → Install app").
 *  - iOS Safari: static instructions (Apple doesn't allow programmatic install).
 *
 * The deferred prompt and platform detection are owned by the shared
 * `usePwaInstall` hook so the in-menu "Install app" button stays in sync.
 *
 * Hidden ONLY when the app is already running in standalone mode (i.e. the
 * user actually installed it) or for 1 h after the user taps "Not now".
 */
export default function PwaInstallBanner() {
  const { standalone, canPrompt, ios, android, install } = usePwaInstall();
  const [dismissedUntil, setDismissedUntil] = useState(() => readDismissedUntil());
  const [waited, setWaited] = useState(false);

  // Give the browser ~2.5 s to fire `beforeinstallprompt` before we fall back
  // to manual-instructions mode. Without this delay we'd flash the manual
  // banner first and replace it with the one-tap banner a beat later, which
  // looks broken.
  useEffect(() => {
    const t = window.setTimeout(() => setWaited(true), ios ? 2200 : 2800);
    return () => window.clearTimeout(t);
  }, [ios]);

  if (standalone) return null;
  if (Date.now() < dismissedUntil) return null;
  if (!canPrompt && !waited) return null; // wait for BIP before deciding manual fallback

  const dismiss = () => {
    const until = Date.now() + DISMISS_MS;
    try { localStorage.setItem(DISMISS_KEY, String(until)); } catch { /* quota */ }
    setDismissedUntil(until);
  };

  const onInstallClick = async () => {
    const { outcome } = await install();
    if (outcome !== "accepted") dismiss();
  };

  if (canPrompt) {
    return (
      <BannerShell
        icon={<Download size={18} className="text-white" />}
        title="Install Shubh Vivah"
        onDismiss={dismiss}
        accept={{ onClick: onInstallClick, label: "Install", testid: "pwa-install-accept" }}
        testid="pwa-install-banner"
      >
        Add the catalog to your home screen. Loads faster, no browser bars.
      </BannerShell>
    );
  }

  if (ios) {
    return (
      <BannerShell
        icon={<Share size={18} className="text-white" />}
        title="Install on iPhone"
        onDismiss={dismiss}
        testid="pwa-install-banner-ios"
      >
        Tap the <span className="font-medium text-[#D62828]">Share</span> icon below, then choose <span className="font-medium text-[#D62828]">Add to Home Screen</span>. Or use <span className="font-medium text-[#D62828]">Menu → Install app</span> above.
      </BannerShell>
    );
  }

  // Android (manual) and unknown desktop browsers
  return (
    <BannerShell
      icon={<MoreVertical size={18} className="text-white" />}
      title="Install Shubh Vivah"
      onDismiss={dismiss}
      testid={android ? "pwa-install-banner-android-manual" : "pwa-install-banner-desktop"}
    >
      Open the site <span className="font-medium text-[#D62828]">Menu → Install app</span> for step-by-step instructions, or use your browser&apos;s <span className="font-medium text-[#D62828]">⋮ menu → Install app</span>.
    </BannerShell>
  );
}

// Shared banner chrome — defined OUTSIDE the parent component so React doesn't
// remount the whole subtree on every render.
function BannerShell({ icon, title, children, accept, onDismiss, testid }) {
  return (
    <div
      className="fixed bottom-4 left-4 right-4 md:left-auto md:right-6 md:bottom-6 md:max-w-sm z-40 bg-white border border-[#F4A100] shadow-lg p-4 flex items-start gap-3 sv-anim-in"
      role="dialog"
      data-testid={testid}
    >
      <div className="w-10 h-10 bg-[#D62828] flex items-center justify-center flex-shrink-0">
        {icon}
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-serif text-base text-[#D62828]">{title}</div>
        <div className="text-xs text-[#4A4A4A] leading-relaxed mt-0.5">{children}</div>
        {accept && (
          <div className="flex gap-2 mt-2.5">
            <button
              onClick={accept.onClick}
              data-testid={accept.testid}
              type="button"
              className="bg-[#D62828] hover:bg-[#A41C1C] text-white px-4 py-1.5 tracking-luxe text-[10px]"
            >
              {accept.label}
            </button>
            <button
              onClick={onDismiss}
              data-testid="pwa-install-dismiss"
              type="button"
              className="border border-[#E5E0D8] hover:border-[#D62828] text-[#4A4A4A] px-3 py-1.5 tracking-luxe text-[10px]"
            >
              Not now
            </button>
          </div>
        )}
      </div>
      <button onClick={onDismiss} type="button" className="p-1 text-[#F4A100] hover:text-[#D62828]" aria-label="Close"><X size={16} /></button>
    </div>
  );
}
